// src/services/GeofenceService.js
// Mesmo padrão de registerPlugin do workoutKit.service.js existente.

import { registerPlugin } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import {
  getFirestore, collection, doc, addDoc, query, where,
  orderBy, limit, getDocs, updateDoc, Timestamp, serverTimestamp,
} from 'firebase/firestore';

const GeofenceNative = registerPlugin('Geofence');

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

// ── Constantes de negócio ──────────────────────────────────────────────────────
const MIN_PRESENCE_MINUTES   = 30;
const EXIT_TOLERANCE_MS      = 10 * 60 * 1000;  // 10 min — tolerância de saída
const PRESENCE_EXPIRY_DAYS   = 3;
const MAX_PRESENCES          = 3;
const POPUP_AFTER_MINUTES    = 5;
const MAX_REGIONS_IOS        = 20;
const MAX_REGIONS_ANDROID    = 100;

// ── Estado em memória ──────────────────────────────────────────────────────────
const _enterTimes      = {};  // { regionId: timestampMs }
const _exitTimers      = {};  // { regionId: timeoutId }
const _popupShownToday = {};  // { `${uid}_${gymCode}`: 'YYYY-MM-DD' }
let   _listenersActive = false;
let   _currentUid      = null;

// ─────────────────────────────────────────────────────────────────────────────

export const GeofenceService = {

  // ── Inicialização ─────────────────────────────────────────────────────────

  async init(uid) {
    if (!Capacitor.isNativePlatform()) return { started: false };
    _currentUid = uid;
    try {
      await this._loadAndRegisterAcademies();
      this._startListeners(uid);
      return { started: true };
    } catch (err) {
      console.error('[GeofenceService] init error:', err);
      return { started: false, error: err.message };
    }
  },

  // ── Permissão ─────────────────────────────────────────────────────────────

  async requestPermission() {
    if (!Capacitor.isNativePlatform()) return { status: 'web' };
    try { return await GeofenceNative.requestPermission(); }
    catch (err) { return { status: 'error', error: err.message }; }
  },

  async getPermissionStatus() {
    if (!Capacitor.isNativePlatform()) return { status: 'web' };
    try { return await GeofenceNative.getPermissionStatus(); }
    catch { return { status: 'unknown' }; }
  },

  // ── Carrega academias do Supabase e registra no plugin ────────────────────

  async _loadAndRegisterAcademies() {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/gym_partner_profiles` +
      `?select=referral_code,geo_lat,geo_lng,geo_radius_meters` +
      `&qr_enabled=eq.true&geo_lat=not.is.null&geo_lng=not.is.null`,
      {
        headers: {
          'apikey':        SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!res.ok) throw new Error('Falha ao carregar academias do Supabase');
    const academies = await res.json();
    if (!academies.length) return;

    // Ordena por distância e limita conforme a plataforma
    const maxRegions = Capacitor.getPlatform() === 'ios' ? MAX_REGIONS_IOS : MAX_REGIONS_ANDROID;
    const userLoc    = await this._getUserLocation();

    const sorted = userLoc
      ? [...academies]
          .sort((a, b) =>
            this._haversineMeters(userLoc.lat, userLoc.lng, a.geo_lat, a.geo_lng) -
            this._haversineMeters(userLoc.lat, userLoc.lng, b.geo_lat, b.geo_lng)
          )
          .slice(0, maxRegions)
      : academies.slice(0, maxRegions);

    const regions = sorted.map(a => ({
      id:     a.referral_code,
      lat:    a.geo_lat,
      lng:    a.geo_lng,
      radius: a.geo_radius_meters || 500,
    }));

    await GeofenceNative.registerRegions({ regions });
    // console.log(`[GeofenceService] ${regions.length} academia(s) registrada(s) para monitoramento`);
  },

  // ── Escuta eventos do plugin nativo ───────────────────────────────────────

  _startListeners(uid) {
    if (_listenersActive) return;
    _listenersActive = true;

    GeofenceNative.addListener('onRegionEnter', ({ regionId, timestamp }) => {
      this._handleEnter(uid, regionId, timestamp ?? Date.now());
    });

    GeofenceNative.addListener('onRegionExit', ({ regionId, timestamp }) => {
      this._handleExit(uid, regionId, timestamp ?? Date.now());
    });
  },

  // ── Lógica de entrada na região ───────────────────────────────────────────

  _handleEnter(uid, regionId, timestamp) {
    // console.log(`[GeofenceService] Entrou: ${regionId}`);

    // Se há um timer de saída pendente, o usuário voltou dentro da tolerância
    if (_exitTimers[regionId]) {
      clearTimeout(_exitTimers[regionId]);
      delete _exitTimers[regionId];
      // Continua contando o tempo — não reseta o enterTime
      // console.log(`[GeofenceService] Retornou dentro da tolerância: ${regionId}`);
      return;
    }

    _enterTimes[regionId] = timestamp;

    // Popup após 5 min de permanência
    setTimeout(() => {
      this._maybeShowPopup(uid, regionId);
    }, POPUP_AFTER_MINUTES * 60 * 1000);
  },

  // ── Lógica de saída da região ─────────────────────────────────────────────

  _handleExit(uid, regionId, timestamp) {
    // console.log(`[GeofenceService] Saiu: ${regionId}`);

    if (!_enterTimes[regionId]) return; // nunca entrou — ignorar

    // Aguarda EXIT_TOLERANCE_MS antes de finalizar (10 min)
    _exitTimers[regionId] = setTimeout(async () => {
      delete _exitTimers[regionId];

      const enteredAt = _enterTimes[regionId];
      delete _enterTimes[regionId];

      const exitedAt       = timestamp;
      const durationMs     = exitedAt - enteredAt;
      const durationMinutes = Math.floor(durationMs / 60000);

      // console.log(`[GeofenceService] Permanência em ${regionId}: ${durationMinutes} min`);

      if (durationMinutes >= MIN_PRESENCE_MINUTES) {
        await this._writePresence(uid, regionId, enteredAt, exitedAt, durationMinutes);
      }
    }, EXIT_TOLERANCE_MS);
  },

  // ── Grava presença validada no Firestore ──────────────────────────────────

  async _writePresence(uid, gymCode, enteredAtMs, exitedAtMs, durationMinutes) {
    try {
      const db  = getFirestore();
      const ref = collection(db, 'PACIENTE', uid, 'GYM_PRESENCE');

      // Checa limite de presencas acumuladas não consumidas
      const q = query(
        ref,
        where('VALIDATED', '==', true),
        where('CONSUMED',  '==', false),
        where('EXPIRES_AT', '>', Timestamp.now()),
        orderBy('EXPIRES_AT', 'asc'),
        limit(MAX_PRESENCES + 1)
      );

      const snap = await getDocs(q);

      if (snap.size >= MAX_PRESENCES) {
        // Remove a mais antiga para abrir espaço
        await updateDoc(snap.docs[0].ref, {
          CONSUMED:        true,
          CONSUMED_REASON: 'max_exceeded',
        });
      }

      const expiresAt = new Date(enteredAtMs + PRESENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

      await addDoc(ref, {
        GYM_CODE:         gymCode,
        ENTERED_AT:       Timestamp.fromMillis(enteredAtMs),
        EXITED_AT:        Timestamp.fromMillis(exitedAtMs),
        DURATION_MINUTES: durationMinutes,
        VALIDATED:        true,
        CONSUMED:         false,
        EXPIRES_AT:       Timestamp.fromDate(expiresAt),
        CREATED_AT:       serverTimestamp(),
      });

      // console.log(`[GeofenceService] Presença gravada: ${gymCode} — ${durationMinutes} min`);
    } catch (err) {
      console.error('[GeofenceService] _writePresence error:', err);
    }
  },

  // ── Popup "você está na academia" ─────────────────────────────────────────

  _maybeShowPopup(uid, gymCode) {
    const today = new Date().toISOString().split('T')[0];
    const key   = `${uid}_${gymCode}`;

    // Só exibe uma vez por dia por academia
    if (_popupShownToday[key] === today) return;

    // Só exibe se ainda estiver dentro da região
    if (!_enterTimes[gymCode]) return;

    _popupShownToday[key] = today;

    // Dispara CustomEvent para o app exibir o popup (mesmo padrão do FCMService)
    window.dispatchEvent(new CustomEvent('lesvita:gym-presence-detected', {
      detail: { gymCode, uid },
    }));
  },

  // ── API pública para RegistrarTreinoCheckin ───────────────────────────────

  /**
   * Verifica se o usuário tem presença validada acumulada disponível.
   * Retorna o documento mais antigo não consumido, ou null.
   */
  async getValidatedPresence(uid) {
    try {
      const db = getFirestore();
      const q  = query(
        collection(db, 'PACIENTE', uid, 'GYM_PRESENCE'),
        where('VALIDATED',  '==', true),
        where('CONSUMED',   '==', false),
        where('EXPIRES_AT', '>',  Timestamp.now()),
        orderBy('EXPIRES_AT', 'asc'),
        limit(1)
      );

      const snap = await getDocs(q);
      if (snap.empty) return null;

      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    } catch (err) {
      console.error('[GeofenceService] getValidatedPresence error:', err);
      return null;
    }
  },

  /**
   * Marca uma presença como consumida (chamado após registrar check-in).
   */
  async consumePresence(uid, presenceId) {
    try {
      const db  = getFirestore();
      const ref = doc(db, 'PACIENTE', uid, 'GYM_PRESENCE', presenceId);
      await updateDoc(ref, {
        CONSUMED:    true,
        CONSUMED_AT: serverTimestamp(),
      });
    } catch (err) {
      console.error('[GeofenceService] consumePresence error:', err);
    }
  },

  /**
   * Verifica quantos check-ins não validados o usuário já fez hoje.
   * Limite: MAX_DAILY_UNVALIDATED = 2
   */
  async getDailyUnvalidatedCount(uid) {
    try {
      const db    = getFirestore();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, 'PACIENTE', uid, 'PLANO_NUTRICIONAL', 'CURRENT', 'REGISTRO_TREINOS'),
        where('checkin_at',    '>=', Timestamp.fromDate(today)),
        where('geo_validated', '==', false),
        limit(5)
      );

      const snap = await getDocs(q);
      return snap.size;
    } catch {
      return 0;
    }
  },

  /**
   * Verifica se o usuário está AGORA dentro de uma academia (sem esperar 30 min).
   * Usado para validação direta ao abrir o check-in.
   */
  isCurrentlyInsideAcademy() {
    return Object.keys(_enterTimes).length > 0
      ? Object.keys(_enterTimes)[0]  // gymCode do primeiro match
      : null;
  },

  async openSettings() {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await GeofenceNative.openSettings();
    } catch {
      if (Capacitor.getPlatform() === 'ios') window.open('app-settings:');
    }
  },

  // ── Cleanup ───────────────────────────────────────────────────────────────

  async cleanup() {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await GeofenceNative.removeAllListeners();
    } catch {}
    _listenersActive = false;
    Object.values(_exitTimers).forEach(t => clearTimeout(t));
  },

  // ── Utilitários ───────────────────────────────────────────────────────────

  async _getUserLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 60000 }
      );
    });
  },

  _haversineMeters(lat1, lng1, lat2, lng2) {
    const R    = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                 Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  },
};